import React, { useState, Component } from 'react';
import { CalendarIcon, MapPinIcon, LinkIcon, CalendarPlusIcon, MoreVerticalIcon, EditIcon, TrashIcon, StarIcon, MusicIcon, PaletteIcon, UtensilsIcon, TrophyIcon, LaptopIcon, UsersIcon, TagIcon, ClipboardCheckIcon, AlertCircleIcon, ClockIcon, DollarSignIcon } from 'lucide-react';
import { EventThumbnail } from './EventThumbnail';
export function EventCard({
  event,
  onEditEvent,
  onDeleteEvent,
  onToggleFavorite
}) {
  const [showOptions, setShowOptions] = useState(false);
  const formatDate = dateString => {
    if (!dateString) return '';
    // Parse the YYYY-MM-DD format directly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    // Create date with explicit year, month (0-indexed), and day
    // Use noon UTC to avoid any timezone crossing issues
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  // Convert 24-hour time format to 12-hour format with am/pm
  const formatTime = timeString => {
    if (!timeString) return '';
    // Parse the HH:MM format
    const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
    // Convert to 12-hour format
    let period = hours >= 12 ? 'pm' : 'am';
    let hours12 = hours % 12;
    if (hours12 === 0) hours12 = 12; // Convert 0 to 12 for 12am
    // Format the time (without showing minutes if they're 00)
    return minutes === 0 ? `${hours12}${period}` : `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
  };
  const getStatusColor = status => {
    switch (status) {
      case 'going':
        return 'bg-green-100 text-green-800';
      case 'waitlisted':
        return 'bg-yellow-100 text-yellow-800';
      case 'interested':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };
  const getCategoryIcon = category => {
    switch (category) {
      case 'music':
        return <MusicIcon className="w-3 h-3 mr-1" data-id="element-68" />;
      case 'art':
        return <PaletteIcon className="w-3 h-3 mr-1" data-id="element-69" />;
      case 'food':
        return <UtensilsIcon className="w-3 h-3 mr-1" data-id="element-70" />;
      case 'sports':
        return <TrophyIcon className="w-3 h-3 mr-1" data-id="element-71" />;
      case 'outdoors':
        return <div className="w-3 h-3 mr-1" data-id="element-72" />;
      case 'tech':
        return <LaptopIcon className="w-3 h-3 mr-1" data-id="element-73" />;
      case 'community':
        return <UsersIcon className="w-3 h-3 mr-1" data-id="element-74" />;
      default:
        return <TagIcon className="w-3 h-3 mr-1" data-id="element-75" />;
    }
  };
  const getCategoryColor = category => {
    switch (category) {
      case 'music':
        return 'bg-purple-100 text-purple-800';
      case 'art':
        return 'bg-pink-100 text-pink-800';
      case 'food':
        return 'bg-orange-100 text-orange-800';
      case 'sports':
        return 'bg-green-100 text-green-800';
      case 'outdoors':
        return 'bg-emerald-100 text-emerald-800';
      case 'tech':
        return 'bg-blue-100 text-blue-800';
      case 'community':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const exportToCalendar = () => {
    // Create calendar URL
    const title = encodeURIComponent(event.name);
    const location = encodeURIComponent(event.location || '');
    const details = encodeURIComponent(event.notes || '');
    // Format date for Google Calendar
    let startDate = '';
    let endDate = '';
    if (event.date) {
      startDate = event.date;
      if (event.time) {
        startDate += `T${event.time}:00`;
      }
      // If there's an end time, use it for the end date
      if (event.endTime) {
        endDate = `${event.date}T${event.endTime}:00`;
      } else {
        // If no end time, use the same date/time as start
        endDate = startDate;
      }
    }
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate.replace(/[-:]/g, '')}/${endDate.replace(/[-:]/g, '')}&details=${details}&location=${location}`;
    // Open in new window
    window.open(googleCalendarUrl, '_blank');
  };
  const handleDeleteClick = () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      onDeleteEvent(event.id);
    }
    setShowOptions(false);
  };
  const handleEditClick = () => {
    onEditEvent(event);
    setShowOptions(false);
  };
  const handleFavoriteClick = () => {
    onToggleFavorite(event.id);
    setShowOptions(false);
  };
  // Check if registration deadline is approaching (within 7 days)
  const isRegistrationDeadlineApproaching = () => {
    if (!event.requiresRegistration || !event.registrationDeadline) return false;
    const today = new Date();
    const deadline = new Date(event.registrationDeadline);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };
  // Format the time display to show start and end times if available
  const formatTimeDisplay = () => {
    if (!event.time) return '';
    if (event.endTime) {
      return `${formatTime(event.time)} - ${formatTime(event.endTime)}`;
    }
    return formatTime(event.time);
  };
  // Display fee information
  const renderFeeInfo = () => {
    if (event.isFree) {
      return 'Free';
    } else if (event.feeAmount) {
      return event.feeAmount;
    } else {
      return 'Paid admission';
    }
  };
  return <div className="bg-white rounded-lg shadow mb-4 overflow-hidden" data-id="element-76">
      {/* Only show the large hero image if it exists and the card view is expanded */}
      {event.image && event.expandedView && <div className="w-full h-48 overflow-hidden" data-id="element-77">
          <img src={event.image} alt={event.name} className="w-full h-full object-cover" data-id="element-78" />
        </div>}
      <div className="p-4" data-id="element-79">
        <div className="flex items-start" data-id="element-80">
          {/* Thumbnail on left side */}
          <div className="mr-3 mt-1" data-id="element-81">
            <EventThumbnail event={event} data-id="element-82" />
          </div>
          {/* Event content */}
          <div className="flex-1" data-id="element-83">
            <div className="flex justify-between items-start" data-id="element-84">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-85">
                {event.name}
              </h3>
              <div className="flex items-center" data-id="element-86">
                {/* Favorite Star Button - Always visible */}
                <button onClick={() => onToggleFavorite(event.id)} className="p-1 rounded-full hover:bg-gray-100 mr-1" aria-label={event.isFavorite ? 'Remove from favorites' : 'Add to favorites'} data-id="element-87">
                  <StarIcon className={`w-5 h-5 ${event.isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} data-id="element-88" />
                </button>
                <div className="relative" data-id="element-89">
                  <button className="p-1 rounded-full hover:bg-gray-100" onClick={() => setShowOptions(!showOptions)} data-id="element-90">
                    <MoreVerticalIcon className="w-5 h-5 text-gray-500" data-id="element-91" />
                  </button>
                  {showOptions && <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-92">
                      <div className="py-1" data-id="element-93">
                        <button onClick={handleFavoriteClick} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-94">
                          <StarIcon className={`w-4 h-4 mr-2 ${event.isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} data-id="element-95" />
                          {event.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                        </button>
                        <button onClick={handleEditClick} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-96">
                          <EditIcon className="w-4 h-4 mr-2" data-id="element-97" />
                          Edit Event
                        </button>
                        <button onClick={handleDeleteClick} className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left" data-id="element-98">
                          <TrashIcon className="w-4 h-4 mr-2" data-id="element-99" />
                          Delete Event
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-id="element-100">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`} data-id="element-101">
                {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
              </span>
              {event.category && <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(event.category)}`} data-id="element-102">
                  {getCategoryIcon(event.category)}
                  {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                </span>}
              {event.requiresRegistration && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-103">
                  <ClipboardCheckIcon className="w-3 h-3 mr-1" data-id="element-104" />
                  Registration Required
                </span>}
              {/* Fee badge */}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${event.isFree ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-105">
                <DollarSignIcon className="w-3 h-3 mr-1" data-id="element-106" />
                {renderFeeInfo()}
              </span>
            </div>
            {event.date && <div className="flex items-center mt-2 text-sm text-gray-500" data-id="element-107">
                <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-108" />
                <p data-id="element-109">{formatDate(event.date)}</p>
              </div>}
            {event.time && <div className="flex items-center mt-2 text-sm text-gray-500" data-id="element-110">
                <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-111" />
                <p data-id="element-112">{formatTimeDisplay()}</p>
              </div>}
            {event.requiresRegistration && event.registrationDeadline && <div className={`flex items-center mt-2 text-sm ${isRegistrationDeadlineApproaching() ? 'text-red-600' : 'text-gray-500'}`} data-id="element-113">
                {isRegistrationDeadlineApproaching() ? <AlertCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-red-500" data-id="element-114" /> : <ClipboardCheckIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-115" />}
                <p data-id="element-116">
                  Register by: {formatDate(event.registrationDeadline)}
                  {isRegistrationDeadlineApproaching() && ' (Soon!)'}
                </p>
              </div>}
            {event.location && <div className="flex items-center mt-2 text-sm text-gray-500" data-id="element-117">
                <MapPinIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-118" />
                <p data-id="element-119">{event.location}</p>
              </div>}
            {event.notes && <div className="mt-2 text-sm text-gray-500" data-id="element-120">
                <p className="line-clamp-2" data-id="element-121">{event.notes}</p>
              </div>}
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6 flex justify-between" data-id="element-122">
        {event.link ? <a href={event.link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-500 flex items-center" data-id="element-123">
            <LinkIcon className="w-4 h-4 mr-1" data-id="element-124" />
            View Details
          </a> : <div data-id="element-125"></div>}
        <button onClick={exportToCalendar} className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-126">
          <CalendarPlusIcon className="w-4 h-4 mr-1" data-id="element-127" />
          Add to Calendar
        </button>
      </div>
    </div>;
}